import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useNotificationContext } from '../context/NotificationContext'
import { useCallContext } from '../context/CallContext'
import { usePresence } from '../hooks/usePresence'
import { useMultiSelect } from '../hooks/useMultiSelect'
import { attachmentIsImage, attachmentIsAudio, fallbackCopy } from '../utils/attachments'
import { ArrowLeftIcon, UserIcon, LoaderIcon, ImageIcon, XIcon, ReplyIcon, PlusIcon, TrashIcon, HashIcon, BellIcon, BellOffIcon, DownloadIcon, PaperclipIcon, EditIcon, CheckIcon, ChevronDownIcon, CopyIcon, PhoneIcon, VideoIcon } from '../icons'
import { PresenceDot } from './PresenceDot'
import { WhatsAppAudioPlayer } from './WhatsAppAudioPlayer'
import { EmojiPicker, QUICK_REACTIONS } from './EmojiPicker'
import { ChatInputBar } from './ChatInputBar'
import { CallView } from './CallView'
import { avatarUrl } from '../utils/avatar'
import { linkifyText } from '../utils/linkify'
import type { GroupChannelMember, GroupChannelMessage, MessageReaction, PublicUser, WorkspaceChannel } from '../types'
import { API_URL } from '../api'
import { getToken } from '../api'


// Render message text with @mentions highlighted in blue.
// Matches @member_name (including names with spaces) and @all/@atlas.
function renderGroupMentions(text: string, members: GroupChannelMember[]) {
  if (!text) return null

  // Build a list of mentionable names: special entries + member names
  // Sort by length descending so "Pedro Victor Couto" matches before "Pedro"
  const mentionNames = [
    { name: 'all', display: 'all', isSpecial: true },
    { name: 'atlas', display: 'atlas', isSpecial: true },
    { name: 'everyone', display: 'everyone', isSpecial: true },
    { name: 'channel', display: 'channel', isSpecial: true },
    ...members.map((m) => ({
      name: m.name || m.email || '',
      display: m.display_name || m.name || m.email || '',
      userId: m.user_id || m.id,
      isSpecial: false,
    })).filter((m) => m.name),
  ].sort((a, b) => b.name.length - a.name.length)

  // Find all @ positions and try to match against known names
  const parts: Array<{ type: string; value: string; key?: number }> = []
  let lastIndex = 0
  let i = 0
  while (i < text.length) {
    if (text[i] === '@' && (i === 0 || /\s/.test(text[i - 1]))) {
      // Try to match a member name starting at i+1
      const remaining = text.slice(i + 1)
      let matched: { entry: { name: string; display: string; userId?: string; isSpecial: boolean }; fullText: string } | null = null
      for (const entry of mentionNames) {
        const candidate = entry.name
        if (remaining.toLowerCase().startsWith(candidate.toLowerCase())) {
          // Check that the match ends at a word boundary (space, punctuation, end)
          const afterMatch = remaining.slice(candidate.length)
          if (afterMatch.length === 0 || /^[\s.,;:!?\n]/.test(afterMatch)) {
            matched = { entry, fullText: text.slice(i + 1, i + 1 + candidate.length) }
            break
          }
        }
        // Also try display_name if different from name
        if (entry.display && entry.display !== entry.name) {
          const candidateD = entry.display
          if (remaining.toLowerCase().startsWith(candidateD.toLowerCase())) {
            const afterMatch = remaining.slice(candidateD.length)
            if (afterMatch.length === 0 || /^[\s.,;:!?\n]/.test(afterMatch)) {
              matched = { entry, fullText: text.slice(i + 1, i + 1 + candidateD.length) }
              break
            }
          }
        }
      }
      if (matched) {
        // Push text before the mention
        if (i > lastIndex) {
          parts.push({ type: 'text', value: text.slice(lastIndex, i) })
        }
        parts.push({ type: 'mention', value: '@' + matched.fullText, key: i })
        lastIndex = i + 1 + matched.fullText.length
        i = lastIndex
        continue
      }
    }
    i++
  }
  // Push remaining text
  if (lastIndex < text.length) {
    parts.push({ type: 'text', value: text.slice(lastIndex) })
  }

  if (parts.length === 0) return linkifyText(text)
  return parts.map((p, idx) => {
    if (p.type === 'mention') {
      return <span key={p.key} className="mention-tag">{p.value}</span>
    }
    return <span key={idx}>{linkifyText(p.value)}</span>
  })
}

export function GroupChannelView({ channelId: propChannelId }: { channelId?: string }) {
  const routeParams = useParams()
  const channelId = propChannelId || routeParams.channelId
  const navigate = useNavigate()
  const { token, user } = useAuth()
  const { mutedUsers, muteUser, unmuteUser, fetchUnreadCount } = useNotificationContext()
  const call = useCallContext()
  const presence = usePresence()
  const [messages, setMessages] = useState<GroupChannelMessage[]>([])
  const [members, setMembers] = useState<GroupChannelMember[]>([])
  const [channelInfo, setChannelInfo] = useState<(WorkspaceChannel & { workspace_name?: string }) | null>(null)
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [replyTo, setReplyTo] = useState<GroupChannelMessage | null>(null)
  const [editingMsg, setEditingMsg] = useState<string | null>(null)
  const [editText, setEditText] = useState('')
  const [msgOptionsMenu, setMsgOptionsMenu] = useState<{ msgId: string; x: number; y: number; canCopy: boolean; canEdit: boolean; canDelete: boolean } | null>(null)
  const multiSelect = useMultiSelect()
  const [showMembers, setShowMembers] = useState(false)
  const [showAddMember, setShowAddMember] = useState(false)
  const [allUsers, setAllUsers] = useState<PublicUser[]>([])
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; userId: string; name?: string } | null>(null)
  const [previewAttachment, setPreviewAttachment] = useState<{ url: string; name: string; type: string } | null>(null)
  const [reactionPickerFor, setReactionPickerFor] = useState<{ msgId: string; x: number; y: number; full?: boolean } | null>(null)
  const [activeChannelCall, setActiveChannelCall] = useState<{ call_id: string; is_video: boolean; participants: string[]; channel_id?: string } | null>(null) // { call_id, is_video, participants } when channel has active call
  const messagesEndRef = useRef<HTMLDivElement | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const loadingMoreRef = useRef(false)
  const hasMoreRef = useRef(true)

  const scrollToMessage = useCallback((msgId: string) => {
    const container = scrollContainerRef.current
    if (!container) return
    const el = container.querySelector(`[data-msg-id="${msgId}"]`)
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    el.classList.add('msg-highlight')
    setTimeout(() => el.classList.remove('msg-highlight'), 2000)
  }, [])

  const authHeaders = useMemo(() => (token ? { 'Authorization': `Bearer ${token}` } : {}) as Record<string, string>, [token])

  // Clear multi-select when switching channels
  useEffect(() => { multiSelect.clear() }, [channelId])

  // Check for active channel call on load and when channel changes
  // Retries indefinitely with exponential backoff so the app recovers
  // even after long network outages (e.g. 1h without internet).
  const checkActiveCallRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const checkActiveCall = useCallback(async (retryCount = 0) => {
    if (!channelId || channelId === 'null' || channelId === 'undefined') return
    try {
      const res = await fetch(`${API_URL}/api/group-channel/${channelId}/call`, { headers: authHeaders })
      if (!res.ok) return
      const data = await res.json()
      if (data.active) {
        setActiveChannelCall(data)
      } else {
        setActiveChannelCall(null)
      }
    } catch (err: unknown) {
      // Network error — retry with exponential backoff (2s, 4s, 8s, ... max 30s)
      // Keep retrying forever so we recover when internet comes back.
      const delay = Math.min(2000 * Math.pow(2, retryCount), 30000)
      if (checkActiveCallRef.current) clearTimeout(checkActiveCallRef.current)
      checkActiveCallRef.current = setTimeout(() => checkActiveCall(retryCount + 1), delay)
    }
  }, [channelId, authHeaders])

  useEffect(() => {
    checkActiveCall(0)
    // When network comes back online, immediately retry
    const handleOnline = () => {
      if (checkActiveCallRef.current) clearTimeout(checkActiveCallRef.current)
      checkActiveCall(0)
    }
    window.addEventListener('online', handleOnline)
    return () => {
      window.removeEventListener('online', handleOnline)
      if (checkActiveCallRef.current) clearTimeout(checkActiveCallRef.current)
    }
  }, [checkActiveCall])

  // Listen for channel-call-started notifications
  useEffect(() => {
    const handleChannelCallStarted = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail?.channel_id === channelId) {
        setActiveChannelCall({
          call_id: detail.call_id,
          is_video: detail.is_video,
          participants: [],
        })
      }
    }
    window.addEventListener('channel-call-started', handleChannelCallStarted)
    return () => window.removeEventListener('channel-call-started', handleChannelCallStarted)
  }, [channelId])

  // Re-check active call when our call state changes (someone joined/left)
  useEffect(() => {
    if (call.callState === 'idle' || call.callState === 'failed') {
      checkActiveCall()
    }
  }, [call.callState, checkActiveCall])

  const loadMessages = useCallback(async () => {
    if (!channelId || channelId === 'null' || channelId === 'undefined') return
    try {
      const res = await fetch(`${API_URL}/api/group-channel/${channelId}/messages`, { headers: authHeaders })
      if (!res.ok) return
      const data = await res.json()
      const msgs = data.messages || []
      setMessages(msgs)
      hasMoreRef.current = msgs.length >= 20
    } catch {
      // ignore
    }
  }, [channelId, authHeaders])

  const loadOlderMessages = useCallback(async () => {
    if (!channelId || channelId === 'null' || channelId === 'undefined' || loadingMoreRef.current || !hasMoreRef.current) return
    if (messages.length === 0) return
    loadingMoreRef.current = true
    setLoadingMore(true)
    const container = scrollContainerRef.current
    const prevScrollHeight = container?.scrollHeight || 0
    const prevScrollTop = container?.scrollTop || 0
    try {
      const oldest = messages[0]
      const before = oldest?.created_at
      const res = await fetch(`${API_URL}/api/group-channel/${channelId}/messages?before=${encodeURIComponent(before)}`, { headers: authHeaders })
      if (!res.ok) return
      const data = await res.json()
      const older = data.messages || []
      if (older.length > 0) {
        setMessages(prev => [...older, ...prev])
        requestAnimationFrame(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight
            container.scrollTop = prevScrollTop + (newScrollHeight - prevScrollHeight)
          }
        })
      }
      if (older.length < 20) {
        hasMoreRef.current = false
      }
    } catch {
      // ignore
    } finally {
      loadingMoreRef.current = false
      setLoadingMore(false)
    }
  }, [channelId, authHeaders, messages])

  const loadMembers = useCallback(async () => {
    if (!channelId || channelId === 'null' || channelId === 'undefined') return
    try {
      const res = await fetch(`${API_URL}/api/group-channel/${channelId}/members`, { headers: authHeaders })
      if (!res.ok) return
      const data = await res.json()
      setMembers(data.members || [])
    } catch {
      // ignore
    }
  }, [channelId, authHeaders])

  const loadChannelInfo = useCallback(async () => {
    if (!channelId) return
    try {
      // Get workspace channels from the workspace
      const wsRes = await fetch(`${API_URL}/api/workspaces`, { headers: authHeaders })
      const wsData = await wsRes.json()
      const workspaces = wsData.workspaces || []
      for (const ws of workspaces) {
        const chRes = await fetch(`${API_URL}/api/workspaces/${ws.id}/channels`, { headers: authHeaders })
        const chData = await chRes.json()
        const channels = chData.channels || []
        const found = channels.find((c: WorkspaceChannel) => c.id === channelId)
        if (found) {
          setChannelInfo({ ...found, workspace_name: ws.name })
          break
        }
      }
    } catch {
      // ignore
    }
  }, [channelId, authHeaders])

  const markChannelRead = useCallback(() => {
    if (!channelId || channelId === 'null' || channelId === 'undefined') return
    fetch(`${API_URL}/api/group-channel/${channelId}/read`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
    }).then(res => {
      if (res.ok) {
        fetchUnreadCount?.()
        window.dispatchEvent(new CustomEvent('channel-read', { detail: { channel_id: channelId } }))
      }
    }).catch(() => {})
  }, [channelId, authHeaders, fetchUnreadCount])

  useEffect(() => {
    setLoading(true)
    Promise.all([loadMessages(), loadMembers(), loadChannelInfo()]).finally(() => setLoading(false))
    markChannelRead()
  }, [channelId, loadMessages, loadMembers, loadChannelInfo, markChannelRead])

  const firstLoadRef = useRef(true)
  useEffect(() => {
    if (loading || messages.length === 0) return
    if (loadingMoreRef.current) return
    if (firstLoadRef.current) {
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
      })
      firstLoadRef.current = false
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, loading])

  useEffect(() => {
    firstLoadRef.current = true
    hasMoreRef.current = true
  }, [channelId])

  // Listen for real-time group channel notifications
  useEffect(() => {
    const handleNotification = (e: Event) => {
      const notif = (e as CustomEvent).detail
      if (notif.type === 'group_message' && String(notif.channel_id) === String(channelId) && channelId && channelId !== 'null' && channelId !== 'undefined') {
        loadMessages()
        // Re-mark as read on backend since new messages arrived while viewing
        markChannelRead()
      }
    }
    window.addEventListener('dm-notification', handleNotification)
    return () => window.removeEventListener('dm-notification', handleNotification)
  }, [channelId, loadMessages, markChannelRead])

  // Reload messages when the tab regains visibility/focus — catches up
  // any messages missed while the notification socket was silently dead.
  useEffect(() => {
    const handleVisible = () => {
      if (document.visibilityState === 'visible') loadMessages()
    }
    document.addEventListener('visibilitychange', handleVisible)
    window.addEventListener('focus', handleVisible)
    return () => {
      document.removeEventListener('visibilitychange', handleVisible)
      window.removeEventListener('focus', handleVisible)
    }
  }, [loadMessages])

  // Close context menu on click
  useEffect(() => {
    const closeMenu = () => setContextMenu(null)
    if (contextMenu) {
      window.addEventListener('click', closeMenu)
      return () => window.removeEventListener('click', closeMenu)
    }
  }, [contextMenu])

  // Real-time updates are handled via WebSocket (dm-notification event above).
  // Polling removed to avoid resetting pagination state when older messages are loaded.

  // Reactions are now embedded in messages (m.reactions) and synced via WebSocket.
  // Listen for real-time reaction updates.
  useEffect(() => {
    const handleReactionUpdate = (e: Event) => {
      const { message_type, message_id, reactions: newReactions } = (e as CustomEvent).detail || {}
      if (message_type !== 'channel') return
      setMessages(prev => prev.map(m =>
        m.id === message_id ? { ...m, reactions: newReactions || [] } : m
      ))
    }
    window.addEventListener('reaction-update', handleReactionUpdate)
    return () => window.removeEventListener('reaction-update', handleReactionUpdate)
  }, [])

  // Close message options menu on click outside
  useEffect(() => {
    const closeOptions = () => setMsgOptionsMenu(null)
    if (msgOptionsMenu) {
      window.addEventListener('click', closeOptions)
      window.addEventListener('scroll', closeOptions, true)
      return () => {
        window.removeEventListener('click', closeOptions)
        window.removeEventListener('scroll', closeOptions, true)
      }
    }
  }, [msgOptionsMenu])

  // Listen for real-time message edits
  useEffect(() => {
    const handleChannelEdited = (e: Event) => {
      const msg = (e as CustomEvent).detail?.message
      if (!msg) return
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, content: msg.content } : m))
    }
    window.addEventListener('channel_edited', handleChannelEdited)
    return () => window.removeEventListener('channel_edited', handleChannelEdited)
  }, [])

  // Listen for real-time message deletions
  useEffect(() => {
    const handleChannelDeleted = (e: Event) => {
      const msgId = (e as CustomEvent).detail?.message_id
      if (!msgId) return
      setMessages(prev => prev.filter(m => m.id !== msgId))
    }
    window.addEventListener('channel_deleted', handleChannelDeleted)
    return () => window.removeEventListener('channel_deleted', handleChannelDeleted)
  }, [])

  // Listen for real-time bulk message deletions
  useEffect(() => {
    const handleChannelDeletedBulk = (e: Event) => {
      const ids = (e as CustomEvent).detail?.message_ids
      if (!ids || !Array.isArray(ids)) return
      const idSet = new Set(ids)
      setMessages(prev => prev.filter(m => !idSet.has(m.id)))
    }
    window.addEventListener('channel_deleted_bulk', handleChannelDeletedBulk)
    return () => window.removeEventListener('channel_deleted_bulk', handleChannelDeletedBulk)
  }, [])

  const handleEditMessage = async (msgId: string, newContent: string) => {
    if (!channelId || channelId === 'null' || channelId === 'undefined') return
    try {
      const res = await fetch(`${API_URL}/api/group-channel/${channelId}/messages/${msgId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ content: newContent }),
      })
      if (!res.ok) throw new Error('Failed to edit')
      const updated = await res.json()
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, content: updated.content } : m))
      setEditingMsg(null)
      setEditText('')
    } catch (err: unknown) {
      console.error('Edit failed:', err)
      setEditingMsg(null)
      setEditText('')
    }
  }

  const startEditing = (msg: GroupChannelMessage) => {
    setEditingMsg(msg.id)
    setEditText(msg.content || '')
  }

  const cancelEditing = () => {
    setEditingMsg(null)
    setEditText('')
  }

  const toggleReaction = async (msgId: string, emoji: string) => {
    const msg = messages.find(m => m.id === msgId)
    const msgReactions = msg?.reactions || []
    const existing = msgReactions.find(r => r.emoji === emoji && r.user_id === user?.id)
    try {
      if (existing) {
        const res = await fetch(`${API_URL}/api/reactions`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({ message_type: 'channel', message_id: msgId, emoji }),
        })
        if (res.ok) {
          const data = await res.json()
          setMessages(prev => prev.map(m =>
            m.id === msgId ? { ...m, reactions: data.reactions || [] } : m
          ))
        }
      } else {
        const res = await fetch(`${API_URL}/api/reactions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({ message_type: 'channel', message_id: msgId, emoji }),
        })
        if (res.ok) {
          const data = await res.json()
          setMessages(prev => prev.map(m =>
            m.id === msgId ? { ...m, reactions: data.reactions || [] } : m
          ))
        }
      }
    } catch { /* ignore */ }
    setReactionPickerFor(null)
  }

  const handleSend = async (text: string, files: File[], currentReplyTo: unknown) => {
    const replyToMsg = currentReplyTo as GroupChannelMessage | null
    if (!channelId || channelId === 'null' || channelId === 'undefined') return
    setSending(true)
    try {
      const fileArr = Array.isArray(files) ? files : (files ? [files] : [])

      // Send text first (if any)
      if (text) {
        const res = await fetch(`${API_URL}/api/group-channel/${channelId}/messages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({ content: text, reply_to_id: replyToMsg?.id || null }),
        })
        if (!res.ok) return
      }

      // Send each file as a separate message
      for (const file of fileArr) {
        const formData = new FormData()
        formData.append('content', '')
        formData.append('attachment', file)
        if (replyToMsg) formData.append('reply_to_id', String(replyToMsg.id))
        const res = await fetch(`${API_URL}/api/group-channel/${channelId}/messages`, {
          method: 'POST',
          headers: { ...authHeaders },
          body: formData,
        })
        if (!res.ok) break
      }

      setReplyTo(null)
      loadMessages()
    } catch {
      // ignore
    } finally {
      setSending(false)
    }
  }

  const handleSendAudio = async (file: File) => {
    if (!channelId || channelId === 'null' || channelId === 'undefined') return
    setSending(true)
    try {
      const formData = new FormData()
      formData.append('content', '')
      formData.append('attachment', file)
      const res = await fetch(`${API_URL}/api/group-channel/${channelId}/messages`, {
        method: 'POST',
        headers: { ...authHeaders },
        body: formData,
      })
      if (res.ok) loadMessages()
    } catch {
      // ignore
    } finally {
      setSending(false)
    }
  }

  const handleAddMember = async (userId: string) => {
    if (!channelId || channelId === 'null' || channelId === 'undefined') return
    try {
      await fetch(`${API_URL}/api/group-channel/${channelId}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ user_id: userId }),
      })
      loadMembers()
    } catch {
      // ignore
    }
  }

  const handleRemoveMember = async (userId: string) => {
    if (!channelId || channelId === 'null' || channelId === 'undefined') return
    try {
      await fetch(`${API_URL}/api/group-channel/${channelId}/members?user_id=${userId}`, {
        method: 'DELETE',
        headers: authHeaders,
      })
      loadMembers()
    } catch {
      // ignore
    }
  }

  const loadAllUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/users`, { headers: authHeaders })
      const data = await res.json()
      setAllUsers(data.users || [])
    } catch {
      // ignore
    }
  }

  const formatTime = (ts: string | number | Date | undefined) => {
    if (!ts) return ''
    const d = new Date(ts)
    const now = new Date()
    const isToday = d.toDateString() === now.toDateString()
    if (isToday) {
      return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    }
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  }

  const attUrl = (m: GroupChannelMessage) => {
    const url = m.attachment_url
    if (!url || url === 'uploading') return ''
    if (url.startsWith('https://cdn.colossus7.com/')) return url
    if (url.startsWith('/api/media/')) return 'https://cdn.colossus7.com/' + url.slice('/api/media/'.length)
    if (url.startsWith('http://') || url.startsWith('https://')) return url
    return `${API_URL}/api/attachment/${m.id}?token=${encodeURIComponent(getToken() || '')}`
  }

  const findReplyMessage = (replyToId: string | null | undefined) => {
    if (!replyToId) return null
    return messages.find(m => m.id === replyToId)
  }

  const memberIds = new Set(members.map(m => m.user_id))
  const nonMembers = allUsers.filter(u => !memberIds.has(u.id))

  if (loading) {
    return <div className="hermes-loading">Carregando canal...</div>
  }

  return (
    <>
    <div className="hermes-chat-page group-channel-page">
      <div className="hermes-chat-header">
        <button className="icon-btn" onClick={() => navigate(-1)}>
          <ArrowLeftIcon size={18} />
        </button>
        <div className="hermes-chat-title">
          <HashIcon size={20} />
          <div className="hermes-chat-title-text">
            <span>{channelInfo?.name || `Canal #${channelId}`}</span>
            <span className="presence-text" style={{ color: 'var(--text-muted)' }}>
              {channelInfo?.workspace_name || 'Workspace'} · {members.length} membros
            </span>
          </div>
        </div>
        <div className="dm-header-actions">
          <button
            className="icon-btn"
            onClick={() => channelId && call.startChannelCall(channelId, false)}
            title="Chamada de voz"
            disabled={call.callState !== 'idle' && call.callState !== 'failed'}
          >
            <PhoneIcon size={18} />
          </button>
          <button
            className="icon-btn"
            onClick={() => channelId && call.startChannelCall(channelId, true)}
            title="Chamada de vídeo"
            disabled={call.callState !== 'idle' && call.callState !== 'failed'}
          >
            <VideoIcon size={18} />
          </button>
          <button
            className="icon-btn"
            onClick={() => { setShowMembers(!showMembers); if (!showMembers) loadMembers() }}
            title="Membros"
          >
            <UserIcon size={18} />
          </button>
        </div>
      </div>

      {/* Active channel call banner */}
      {activeChannelCall && call.callState !== 'connected' && call.callState !== 'calling' && (
        <div className="channel-call-banner">
          <div className="channel-call-banner-info">
            <PhoneIcon size={16} />
            <span>
              {activeChannelCall.is_video ? 'Chamada de vídeo' : 'Chamada de voz'} em andamento
              {activeChannelCall.participants?.length > 0 && ` · ${activeChannelCall.participants.length} participante(s)`}
            </span>
          </div>
          <button
            className="channel-call-join-btn"
            onClick={() => call.joinChannelCall(activeChannelCall.call_id, activeChannelCall.is_video, channelId)}
          >
            Entrar
          </button>
        </div>
      )}

      <div className="group-channel-layout">
        {multiSelect.isSelecting && (
          <div className="bulk-action-bar">
            <span className="bulk-action-count">
              {multiSelect.selected.size} selecionada{multiSelect.selected.size !== 1 ? 's' : ''}
            </span>
            <button
              className="bulk-action-btn bulk-action-danger"
              onClick={async () => {
                const ids = [...multiSelect.selected]
                if (!channelId || channelId === 'null' || channelId === 'undefined') return
                if (!confirm(`Apagar ${ids.length} mensagem(ns)?`)) return
                try {
                  await fetch(`${API_URL}/api/group-channel/${channelId}/messages/delete-bulk`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', ...authHeaders },
                    body: JSON.stringify({ message_ids: ids }),
                  })
                  setMessages(prev => prev.filter(m => !ids.includes(m.id)))
                  multiSelect.clear()
                } catch (err: unknown) {
                  console.error('Bulk delete failed:', err)
                }
              }}
            >
              <TrashIcon size={16} /> Apagar
            </button>
            <button className="bulk-action-btn" onClick={() => multiSelect.clear()}>
              <XIcon size={16} /> Cancelar
            </button>
          </div>
        )}
        {/* Embedded channel call — only for the current channel */}
        {call.isChannelCall && (call.callState !== 'idle') && (call.callState !== 'failed') && call.channelId === channelId && (
          <CallView call={call} onClose={() => {}} embedded />
        )}
        <div
          className="hermes-chat-messages"
          ref={scrollContainerRef}
          onScroll={(e) => {
            const el = e.currentTarget
            if (el.scrollTop < 50) loadOlderMessages()
          }}
        >
          {messages.length === 0 ? (
            <div className="hermes-chat-welcome">
              <HashIcon size={48} />
              <h2>Nenhuma mensagem ainda</h2>
              <p>Envie a primeira mensagem no canal {channelInfo?.name}.</p>
            </div>
          ) : (
            <>
            {loadingMore && (
              <div className="hermes-loading-more" style={{ textAlign: 'center', padding: '8px', color: 'var(--text-muted)', fontSize: 13 }}>
                Carregando mensagens antigas...
              </div>
            )}
            {messages.map(m => {
              const isMine = m.sender_id === user?.id
              const repliedMsg = findReplyMessage(m.reply_to_id)
              return (
                <div
                  key={m.id}
                  data-msg-id={m.id}
                  className={`hermes-chat-msg ${isMine ? 'user' : 'assistant'}${multiSelect.selected.has(m.id) ? ' msg-selected' : ''}${multiSelect.isSelecting ? ' msg-selectable' : ''}`}
                  onDoubleClick={() => multiSelect.isSelecting ? null : setReplyTo(m)}
                  onClick={(e) => {
                    if (e.shiftKey || multiSelect.isSelecting) {
                      e.preventDefault()
                      const idx = messages.findIndex(x => x.id === m.id)
                      if (e.shiftKey && multiSelect.lastSelectedIdx.current !== null) {
                        const ids = messages.map(x => x.id)
                        multiSelect.selectRange(ids, multiSelect.lastSelectedIdx.current, idx)
                      } else {
                        multiSelect.toggleSelect(m.id, idx)
                      }
                    }
                  }}
                >
                  {multiSelect.isSelecting && (
                    <div className="msg-select-checkbox" onClick={(e) => {
                      e.stopPropagation()
                      const idx = messages.findIndex(x => x.id === m.id)
                      multiSelect.toggleSelect(m.id, idx)
                    }}>
                      <div className={`msg-checkbox-box ${multiSelect.selected.has(m.id) ? 'checked' : ''}`}>
                        {multiSelect.selected.has(m.id) && <CheckIcon size={14} />}
                      </div>
                    </div>
                  )}
                  <div className="hermes-chat-msg-avatar">
                    {m.sender_avatar ? (
                      <img src={avatarUrl({ avatar_url: m.sender_avatar })} alt="" className="avatar-img" />
                    ) : (
                      <UserIcon size={18} />
                    )}
                  </div>
                  <div className="hermes-chat-msg-body">
                    <div className="hermes-chat-msg-header">
                      <span className="hermes-chat-msg-role">
                        {m.sender_display_name || m.sender_name || `Usuário #${m.sender_id}`}
                      </span>
                      <span className="hermes-chat-msg-time">{formatTime(m.created_at)}</span>
                    </div>
                    {repliedMsg && (
                      <div className="dm-reply-quote" onClick={() => scrollToMessage(repliedMsg.id)}>
                        {repliedMsg.attachment_url && repliedMsg.attachment_url !== 'uploading' && attachmentIsImage(repliedMsg.attachment_type) ? (
                          <img
                            src={attUrl(repliedMsg)}
                            alt={repliedMsg.attachment_name}
                            className="dm-reply-img"
                          />
                        ) : (
                          <span className="dm-reply-text">
                            {repliedMsg.content || (repliedMsg.attachment_name ? `[${repliedMsg.attachment_name}]` : '...')}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="dm-msg-bubble">
                    {m.attachment_url && m.attachment_url !== 'uploading' && attachmentIsImage(m.attachment_type) && (
                      <div className="dm-attachment">
                        <img
                          src={attUrl(m)}
                          alt={m.attachment_name}
                          className="dm-attachment-img"
                          onClick={() => setPreviewAttachment({ url: attUrl(m), name: m.attachment_name, type: m.attachment_type })}
                        />
                      </div>
                    )}
                    {m.attachment_url && m.attachment_url !== 'uploading' && attachmentIsAudio(m.attachment_type) && (
                      <div className="dm-attachment">
                        <WhatsAppAudioPlayer
                          src={attUrl(m)}
                          fileName={m.attachment_name || 'audio'}
                          isOutgoing={isMine}
                        />
                      </div>
                    )}
                    {m.attachment_url && m.attachment_url !== 'uploading' && !attachmentIsImage(m.attachment_type) && !attachmentIsAudio(m.attachment_type) && (
                      <div className="dm-attachment">
                        <button
                          className="dm-attachment-file"
                          onClick={() => setPreviewAttachment({ url: attUrl(m), name: m.attachment_name, type: m.attachment_type })}
                        >
                          <PaperclipIcon size={16} />
                          <span>{m.attachment_name || 'Arquivo'}</span>
                        </button>
                      </div>
                    )}
                    {editingMsg === m.id ? (
                      <div className="msg-edit-inline">
                        <textarea
                          className="msg-edit-textarea"
                          value={editText}
                          onChange={e => setEditText(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleEditMessage(m.id, editText) }
                            if (e.key === 'Escape') cancelEditing()
                          }}
                          autoFocus
                          rows={1}
                        />
                        <div className="msg-edit-actions">
                          <button className="msg-edit-save" onClick={() => handleEditMessage(m.id, editText)}><CheckIcon size={14} /> Salvar</button>
                          <button className="msg-edit-cancel" onClick={cancelEditing}>Cancelar</button>
                        </div>
                      </div>
                    ) : m.content && <div className="hermes-chat-msg-content">{renderGroupMentions(m.content, members)}</div>}
                    {/* Reactions */}
                    {m.reactions && m.reactions.length > 0 && (
                      <div className="message-reactions">
                        {Object.entries(
                          m.reactions.reduce((acc, r) => {
                            if (!acc[r.emoji]) acc[r.emoji] = []
                            acc[r.emoji].push(r)
                            return acc
                          }, {} as Record<string, MessageReaction[]>)
                        ).map(([emoji, users]: [string, MessageReaction[]]) => {
                          const names = users.map((u: MessageReaction) => {
                            if (u.user_id === user?.id) return 'Você'
                            const member = members.find(m => m.user_id === u.user_id)
                            return member?.display_name || member?.name || `Usuário`
                          })
                          return (
                          <button
                            key={emoji}
                            className={`message-reaction ${users.some((u: MessageReaction) => u.user_id === user?.id) ? 'mine' : ''}`}
                            onClick={() => toggleReaction(m.id, emoji)}
                            title={names.join(', ')}
                          >
                            <span>{emoji}</span>
                            <span className="message-reaction-count">{users.length}</span>
                            <span className="message-reaction-names">{names.join(', ')}</span>
                          </button>
                          )
                        })}
                      </div>
                    )}
                    {/* Reaction button on bubble hover */}
                    {reactionPickerFor?.msgId === m.id && !reactionPickerFor?.full ? (
                      <div className={`reaction-quick-wrapper ${isMine ? '' : 'reaction-left'}`}>
                        <EmojiPicker
                          quickOnly
                          x={reactionPickerFor.x}
                          y={reactionPickerFor.y}
                          onSelect={(emoji) => {
                            if (emoji === 'open-picker') {
                              setReactionPickerFor({ msgId: m.id, full: true, x: reactionPickerFor.x, y: reactionPickerFor.y })
                            } else {
                              toggleReaction(m.id, emoji)
                            }
                          }}
                          onClose={() => setReactionPickerFor(null)}
                        />
                      </div>
                    ) : reactionPickerFor?.msgId === m.id && reactionPickerFor?.full ? (
                      <div className={`reaction-quick-wrapper ${isMine ? '' : 'reaction-left'}`}>
                        <EmojiPicker
                          x={reactionPickerFor.x}
                          y={reactionPickerFor.y}
                          onSelect={(emoji) => toggleReaction(m.id, emoji)}
                          onClose={() => setReactionPickerFor(null)}
                        />
                      </div>
                    ) : (
                      <button
                        className="message-reaction-btn"
                        onClick={(e) => { e.stopPropagation(); setReactionPickerFor({ msgId: m.id, x: e.clientX, y: e.clientY }) }}
                        title="Reagir"
                      >
                        <span style={{ fontSize: 14 }}>😊</span>
                      </button>
                    )}
                    {/* Reply button */}
                    <button
                      className="dm-reply-btn"
                      onClick={() => setReplyTo(m)}
                      title="Responder"
                    >
                      <ReplyIcon size={14} />
                    </button>
                    {/* Options dropdown — available for all messages (copy for everyone, delete only for sender) */}
                    {(m.content || m.attachment_url) && editingMsg !== m.id && (
                      <button
                        className="msg-options-btn"
                        onClick={(e) => {
                          e.stopPropagation()
                          const rect = e.currentTarget.getBoundingClientRect()
                          setMsgOptionsMenu({ msgId: m.id, x: rect.right, y: rect.bottom + 4, canCopy: !!m.content, canEdit: isMine && !!m.content, canDelete: isMine })
                        }}
                        title="Opções"
                      >
                        <ChevronDownIcon size={16} />
                      </button>
                    )}
                    </div>
                  </div>
                </div>
              )
            })
            }
            </>
          )}
          {sending && (
            <div className="hermes-chat-msg user">
              <div className="hermes-chat-msg-avatar"><LoaderIcon size={18} /></div>
              <div className="hermes-chat-msg-body">
                <div className="hermes-chat-msg-content" style={{ color: 'var(--text-muted)' }}>Enviando...</div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {showMembers && (
          <div className="group-channel-members-sidebar">
            <div className="channel-contacts-header">
              <h3>Membros ({members.length})</h3>
              <button
                className="icon-btn"
                onClick={() => { setShowAddMember(true); loadAllUsers() }}
                title="Adicionar membro"
              >
                <PlusIcon size={16} />
              </button>
            </div>
            <div className="channel-contacts-list">
              {members.map(m => (
                <div
                  key={m.id}
                  className="channel-contact-item"
                  onContextMenu={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (m.user_id !== user?.id) {
                      setContextMenu({ x: e.clientX, y: e.clientY, userId: m.user_id, name: m.display_name || m.name || m.email })
                    }
                  }}
                >
                  <div className="channel-contact-avatar presence-avatar">
                    {m.avatar_url ? (
                      <img src={avatarUrl(m)} alt="" className="avatar-img" />
                    ) : (
                      <UserIcon size={18} />
                    )}
                    <PresenceDot status={presence.getUserPresence(m.user_id).status} size={9} />
                  </div>
                  <div className="channel-contact-info">
                    <div className="channel-contact-name">
                      {m.display_name || m.name || m.email}
                      {m.user_id === user?.id && <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 4 }}> (você)</span>}
                    </div>
                    <div className="channel-contact-last">{m.email}</div>
                  </div>
                  {m.user_id !== user?.id && (
                    <button
                      className="icon-btn danger"
                      onClick={() => handleRemoveMember(m.user_id)}
                      title="Remover"
                      style={{ opacity: 0.5 }}
                    >
                      <XIcon size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {showAddMember && (
              <div className="group-channel-add-member">
                <div className="channel-contacts-header">
                  <h4>Adicionar membros</h4>
                  <button className="icon-btn" onClick={() => setShowAddMember(false)}>
                    <XIcon size={16} />
                  </button>
                </div>
                {nonMembers.length === 0 ? (
                  <p style={{ padding: 12, color: 'var(--text-muted)', fontSize: 13 }}>Todos os usuários já são membros.</p>
                ) : (
                  <div className="channel-contacts-list">
                    {nonMembers.map(u => (
                      <div key={u.id} className="channel-contact-item">
                        <div className="channel-contact-avatar">
                          {u.avatar_url ? <img src={avatarUrl(u)} alt="" className="avatar-img" /> : <UserIcon size={18} />}
                        </div>
                        <div className="channel-contact-info">
                          <div className="channel-contact-name">{u.display_name || u.name || u.email}</div>
                          <div className="channel-contact-last">{u.email}</div>
                        </div>
                        <button
                          className="icon-btn"
                          onClick={() => handleAddMember(u.id)}
                          title="Adicionar"
                          style={{ color: 'var(--success)' }}
                        >
                          <PlusIcon size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="hermes-chat-input-area">
        <ChatInputBar
          onSend={handleSend}
          onSendAudio={handleSendAudio}
          replyTo={replyTo}
          onCancelReply={() => setReplyTo(null)}
          replyLabel={`Respondendo a ${replyTo?.sender_display_name || replyTo?.sender_name || 'usuário'}`}
          placeholder={`Mensagem para #${channelInfo?.name || 'canal'}`}
          disabled={sending}
          mentionUsers={members}
        />
      </div>

      {contextMenu && (
        <div
          className="dm-context-menu"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="dm-context-header">{contextMenu.name}</div>
          <button
            className="dm-context-item"
            onClick={() => { handleRemoveMember(contextMenu.userId); setContextMenu(null) }}
          >
            <XIcon size={16} /> Remover do canal
          </button>
        </div>
      )}

      {previewAttachment && (
        <div className="dm-preview-overlay" onClick={() => setPreviewAttachment(null)}>
          <div className="dm-preview-modal" onClick={e => e.stopPropagation()}>
            <button className="dm-preview-close" onClick={() => setPreviewAttachment(null)}>
              <XIcon size={20} />
            </button>
            {previewAttachment.type && previewAttachment.type.startsWith('image/') ? (
              <img src={previewAttachment.url} alt={previewAttachment.name} className="dm-preview-img" />
            ) : (
              <div className="dm-preview-file">
                <PaperclipIcon size={32} />
                <span className="dm-preview-file-name">{previewAttachment.name || 'Arquivo'}</span>
                <a href={previewAttachment.url} download={previewAttachment.name} className="btn-primary">
                  <DownloadIcon size={16} /> Baixar
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>

      {/* Message edit dropdown */}
      {msgOptionsMenu && (
        <div
          className="msg-options-dropdown"
          style={{
            position: 'fixed',
            left: Math.min(msgOptionsMenu.x, window.innerWidth - 160),
            top: msgOptionsMenu.y,
            zIndex: 10000,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {msgOptionsMenu.canCopy && (
            <button
              className="msg-options-item"
              onClick={() => {
                const m = messages.find(msg => msg.id === msgOptionsMenu.msgId)
                if (m) {
                  const text = m.content || ''
                  if (navigator.clipboard?.writeText) {
                    navigator.clipboard.writeText(text).catch(() => {
                      fallbackCopy(text)
                    })
                  } else {
                    fallbackCopy(text)
                  }
                }
                setMsgOptionsMenu(null)
              }}
            >
              <CopyIcon size={16} /> Copiar
            </button>
          )}
          {msgOptionsMenu.canEdit && (
            <button
              className="msg-options-item"
              onClick={() => {
                const m = messages.find(msg => msg.id === msgOptionsMenu.msgId)
                if (m) startEditing(m)
                setMsgOptionsMenu(null)
              }}
            >
              <EditIcon size={16} /> Editar
            </button>
          )}
          {msgOptionsMenu.canDelete && (
            <button
              className="msg-options-item msg-options-item-danger"
              onClick={async () => {
                const m = messages.find(msg => msg.id === msgOptionsMenu.msgId)
                if (m && channelId && channelId !== 'null' && channelId !== 'undefined' && confirm('Apagar esta mensagem?')) {
                  try {
                    await fetch(`${API_URL}/api/group-channel/${channelId}/messages/${m.id}`, {
                      method: 'DELETE',
                      headers: authHeaders,
                    })
                    setMessages(prev => prev.filter(x => x.id !== m.id))
                  } catch (err: unknown) {
                    console.error('Failed to delete message:', err)
                  }
                }
                setMsgOptionsMenu(null)
              }}
            >
              <TrashIcon size={16} /> Apagar
            </button>
          )}
        </div>
      )}
    </>
  )
}
